import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject, of } from 'rxjs';
import { filter, takeUntil, switchMap, catchError } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import { InsightsService } from './services/insights.service';
import { VgIconComponent } from './components/vg-icon/vg-icon.component';

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, VgIconComponent],
  template: `
    <!-- Loading / Error Overlay -->
    <div *ngIf="!isAppReady" class="loading-overlay">
      <div class="loading-container">
        <img src="/volvo_logo.png" alt="Volvo" class="volvo-logo" />

        <!-- Estado de erro -->
        <ng-container *ngIf="loadingError; else loadingTpl">
          <div class="loading-text" style="text-align:center;">
            <h2 style="color:#ff6b6b;">Falha ao carregar</h2>
            <p style="color:rgba(255,255,255,0.9); margin-bottom:1.5rem;">{{ loadingError }}</p>
            <button (click)="reload()" class="vg-button vg-button--outline"
              style="color:#fff; border-color:#fff; cursor:pointer;">
              Recarregar
            </button>
          </div>
        </ng-container>

        <!-- Estado de carregamento -->
        <ng-template #loadingTpl>
          <div class="loading-spinner"></div>
          <div class="loading-text">
            <h2>{{ loadingMessage }}</h2>
            <p>Aguarde enquanto preparamos tudo para você...</p>
          </div>
          <div class="loading-progress">
            <div class="progress-bar" [style.width.%]="loadingProgress"></div>
          </div>
        </ng-template>
      </div>
    </div>

    <div *ngIf="isAppReady" style="min-height: 100vh; background-color: var(--vg-color-grey-10); position: relative;">
      <!-- Header -->
      <header class="app-header">
        <div class="vg-container header-inner">
          <!-- Brand: VOLVO | CTINVOIC title -->
          <div class="header-brand">
            <img src="/volvo_logo.png" alt="Volvo" class="header-volvo" />
            <div class="header-divider"></div>
            <div class="header-titles">
              <h1 class="header-title">CTINVOIC<span class="header-title-suffix"> - Solicitação de Notas Fiscais</span></h1>
              <p class="header-subtitle">Sistema de Solicitação de Notas Fiscais</p>
            </div>
          </div>

          <!-- Right: user info + hamburger -->
          <div class="header-right">
            <ng-container *ngIf="currentUser">
              <div class="header-user">
                <div class="header-avatar">
                  <img *ngIf="currentUser.photoUrl"
                       [src]="currentUser.photoUrl"
                       [alt]="currentUser.fullName"
                       style="width:100%;height:100%;object-fit:cover;" />
                  <span *ngIf="!currentUser.photoUrl" class="header-initials">{{ getUserInitials() }}</span>
                </div>
                <div class="header-user-text">
                  <p style="color:var(--vg-color-white);font-weight:var(--vg-font-weight-medium);margin:0;">{{ currentUser.fullName }}</p>
                  <p style="color:rgba(255,255,255,0.7);font-size:var(--vg-font-size-xs);margin:0;">{{ currentUser.mail }}</p>
                </div>
              </div>
            </ng-container>

            <!-- Hamburger (mobile only) -->
            <button class="hamburger-btn" (click)="menuOpen = !menuOpen" [class.is-open]="menuOpen" aria-label="Menu">
              <span class="hamburger-line"></span>
              <span class="hamburger-line"></span>
              <span class="hamburger-line"></span>
            </button>
          </div>
        </div>
      </header>

      <!-- Navigation bar -->
      <nav class="app-nav" [class.nav-open]="menuOpen">
        <div class="vg-container nav-inner">
          <a routerLink="/home" routerLinkActive="vg-button--primary" class="vg-button vg-button--outline" (click)="menuOpen = false">
            <vg-icon name="home"></vg-icon> Página Inicial
          </a>
          <a routerLink="/solicitation-form" routerLinkActive="vg-button--primary" class="vg-button vg-button--outline" (click)="menuOpen = false">
            <vg-icon name="plus"></vg-icon> Nova Solicitação
          </a>
          <a routerLink="/solicitation-query" routerLinkActive="vg-button--primary" class="vg-button vg-button--outline" (click)="menuOpen = false">
            <vg-icon name="search"></vg-icon> Consultar / Aprovar Solicitação
          </a>
          <a *ngIf="canManageManagers" routerLink="/managers" routerLinkActive="vg-button--primary" class="vg-button vg-button--outline" (click)="menuOpen = false">
            <vg-icon name="users"></vg-icon> Gerenciar Aprovadores
          </a>
          <a *ngIf="canManageParametros" routerLink="/parametros-solicitacao" routerLinkActive="vg-button--primary" class="vg-button vg-button--outline" (click)="menuOpen = false">
            <vg-icon name="settings"></vg-icon> Gerenciar Parâmetros
          </a>
          <a routerLink="/estatisticas-nf" routerLinkActive="vg-button--primary" class="vg-button vg-button--outline" (click)="menuOpen = false">
            <vg-icon name="chart-bar"></vg-icon> Estatísticas NF
          </a>
        </div>
      </nav>

      <!-- Mobile overlay (closes menu when clicking outside) -->
      <div *ngIf="menuOpen" class="nav-overlay" (click)="menuOpen = false"></div>

      <!-- Main content -->
      <main class="vg-container main-content">
        <router-outlet></router-outlet>
      </main>
    </div>
  `,
  styles: [`
    a.vg-button--primary {
      background-color: var(--vg-color-primary) !important;
      color: var(--vg-color-white) !important;
      border-color: var(--vg-color-primary) !important;
    }

    /* ── Header ─────────────────────────────────────────────────────────── */
    .app-header {
      background-color: var(--vg-color-primary);
      border-bottom: 1px solid var(--vg-color-grey-20);
    }
    .header-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--vg-spacing-lg) var(--vg-spacing-xl);
    }
    .header-brand {
      display: flex;
      align-items: center;
      gap: var(--vg-spacing-lg);
      min-width: 0;
    }
    .header-volvo {
      height: 72px;
      width: auto;
      object-fit: contain;
      flex-shrink: 0;
      filter: brightness(0) invert(1);
    }
    .header-divider {
      height: 48px;
      width: 1px;
      background-color: rgba(255,255,255,0.2);
      flex-shrink: 0;
    }
    .header-titles {
      min-width: 0;
    }
    .header-title {
      color: var(--vg-color-white);
      font-size: var(--vg-font-size-xl);
      font-weight: var(--vg-font-weight-bold);
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .header-subtitle {
      color: rgba(255,255,255,0.8);
      font-size: var(--vg-font-size-sm);
      margin: 4px 0 0 0;
    }
    .header-right {
      display: flex;
      align-items: center;
      gap: var(--vg-spacing-md);
      flex-shrink: 0;
    }
    .header-user {
      display: flex;
      align-items: center;
      gap: var(--vg-spacing-sm);
    }
    .header-avatar {
      width: 40px;
      height: 40px;
      border-radius: var(--vg-radius-full);
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(255,255,255,0.2);
      flex-shrink: 0;
    }
    .header-initials {
      color: var(--vg-color-white);
      font-weight: var(--vg-font-weight-bold);
      font-size: 16px;
    }
    .header-user-text {
      text-align: right;
    }

    /* ── Hamburger ───────────────────────────────────────────────────────── */
    .hamburger-btn {
      display: none;
      flex-direction: column;
      justify-content: center;
      gap: 5px;
      background: none;
      border: none;
      cursor: pointer;
      padding: 8px;
      border-radius: 4px;
    }
    .hamburger-btn:hover {
      background-color: rgba(255,255,255,0.1);
    }
    .hamburger-line {
      display: block;
      width: 24px;
      height: 2px;
      background-color: var(--vg-color-white);
      border-radius: 2px;
      transition: transform 0.25s ease, opacity 0.25s ease;
    }
    .hamburger-btn.is-open .hamburger-line:nth-child(1) {
      transform: translateY(7px) rotate(45deg);
    }
    .hamburger-btn.is-open .hamburger-line:nth-child(2) {
      opacity: 0;
    }
    .hamburger-btn.is-open .hamburger-line:nth-child(3) {
      transform: translateY(-7px) rotate(-45deg);
    }

    /* ── Navigation bar ─────────────────────────────────────────────────── */
    .app-nav {
      background-color: var(--vg-color-white);
      border-bottom: 1px solid var(--vg-color-grey-20);
      box-shadow: 0 2px 4px rgba(0,0,0,0.06);
    }
    .nav-inner {
      display: flex;
      align-items: center;
      gap: var(--vg-spacing-sm);
      padding: var(--vg-spacing-sm) var(--vg-spacing-lg);
      flex-wrap: wrap;
    }

    /* ── Mobile overlay ─────────────────────────────────────────────────── */
    .nav-overlay {
      position: fixed;
      inset: 0;
      background-color: rgba(0,0,0,0.35);
      z-index: 90;
    }

    /* ── Main content ───────────────────────────────────────────────────── */
    .main-content {
      padding: var(--vg-spacing-xl) var(--vg-spacing-lg);
    }

    /* ── Mobile breakpoint (≤ 768px) ────────────────────────────────────── */
    @media (max-width: 768px) {
      .header-inner {
        padding: var(--vg-spacing-md) var(--vg-spacing-md);
      }
      .header-brand {
        gap: var(--vg-spacing-sm);
      }
      .header-volvo {
        height: 28px;
      }
      .header-divider {
        height: 32px;
      }
      .header-title {
        font-size: var(--vg-font-size-md);
      }
      .header-title-suffix {
        display: none;
      }
      .header-subtitle {
        display: none;
      }
      .header-user-text {
        display: none;
      }
      .header-avatar {
        width: 34px;
        height: 34px;
      }

      /* Hamburger appears on mobile */
      .hamburger-btn {
        display: flex;
      }

      /* Nav hidden by default on mobile */
      .app-nav {
        display: none;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        z-index: 100;
        border-radius: 0 0 8px 8px;
        box-shadow: 0 8px 24px rgba(0,0,0,0.15);
      }
      .app-nav.nav-open {
        display: block;
      }
      .nav-inner {
        flex-direction: column;
        align-items: stretch;
        gap: 6px;
        padding: var(--vg-spacing-md);
      }
      .nav-inner .vg-button {
        width: 100%;
        justify-content: flex-start;
        text-align: left;
      }

      .main-content {
        padding: var(--vg-spacing-md) var(--vg-spacing-sm);
      }
    }

    /* ── Loading Overlay ─────────────────────────────────────────────────── */
    .loading-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, #041E42 0%, #1565C0 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      animation: fadeIn 0.3s ease-in-out;
    }

    .loading-container {
      text-align: center;
      max-width: 400px;
      padding: 3rem;
      animation: slideUp 0.5s ease-out;
    }

    .volvo-logo {
      height: 80px;
      width: auto;
      object-fit: contain;
      margin-bottom: 2rem;
      filter: brightness(0) invert(1);
    }

    .loading-spinner {
      width: 80px;
      height: 80px;
      margin: 0 auto 2rem;
      border: 6px solid rgba(255, 255, 255, 0.2);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    .loading-text h2 {
      color: white;
      font-size: 1.5rem;
      font-weight: 600;
      margin: 0 0 0.5rem 0;
      animation: pulse 2s ease-in-out infinite;
    }

    .loading-text p {
      color: rgba(255, 255, 255, 0.8);
      font-size: 0.95rem;
      margin: 0;
    }

    .loading-progress {
      width: 100%;
      height: 4px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 2px;
      margin-top: 2rem;
      overflow: hidden;
    }

    .progress-bar {
      height: 100%;
      background: linear-gradient(90deg, #4CAF50, #8BC34A);
      border-radius: 2px;
      transition: width 0.5s ease-out;
      box-shadow: 0 0 10px rgba(76, 175, 80, 0.5);
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes slideUp {
      from {
        transform: translateY(30px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.7; }
    }
  `]
})
export class AppComponent implements OnInit, OnDestroy {
  private static readonly HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
  title = 'CTINVOIC - Sistema de Solicitação de Notas Fiscais';
  currentUser: any = null;
  isAppReady = false;
  menuOpen = false;
  canManageManagers = false;
  canManageParametros = false;
  loadingMessage = 'Autenticando...';
  loadingProgress = 0;
  loadingError: string | null = null;
  private readonly destroy$ = new Subject<void>();
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly authService: AuthService,
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly insightsService: InsightsService
  ) {}

  ngOnInit(): void {
    this.updateLoadingState('Autenticando...', 10);

    // handleRedirectObservable processa o redirect do login e popula as contas no MSAL.
    // Só depois que ele completa é seguro chamar getAllAccounts().
    this.msalService.handleRedirectObservable().subscribe({
      complete: () => {
        // Após o redirect ser processado, aguarda o MSAL terminar qualquer interação pendente.
        this.msalBroadcastService.inProgress$
          .pipe(
            filter((status: InteractionStatus) => status === InteractionStatus.None),
            takeUntil(this.destroy$)
          )
          .subscribe(() => {
            this.updateLoadingState('Configurando conta...', 40);
            this.checkAndSetActiveAccount();
            this.setLoginDisplay();
          });
      }
    });
  }

  reload(): void {
    globalThis.location.reload();
  }

  ngOnDestroy(): void {
    this.stopTelemetryHeartbeat();
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  private startTelemetryHeartbeat(userId?: string): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }

    this.heartbeatTimer = setInterval(() => {
      this.insightsService.trackHeartbeat({
        version: '1.0',
        user: userId ?? 'anonymous'
      });
    }, AppComponent.HEARTBEAT_INTERVAL_MS);
  }

  private stopTelemetryHeartbeat(): void {
    if (!this.heartbeatTimer) {
      return;
    }

    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private updateLoadingState(message: string, progress: number): void {
    this.loadingMessage = message;
    this.loadingProgress = progress;
  }

  checkAndSetActiveAccount(): void {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      this.updateLoadingState('Carregando perfil...', 60);
    }
  }

  setLoginDisplay(): void {
    // Verificar se está autenticado no MSAL
    const accounts = this.msalService.instance.getAllAccounts();
    const isLoggedIn = accounts.length > 0;
    
    if (isLoggedIn) {
      this.updateLoadingState('Carregando dados do usuário...', 75);
      this.authService.loadCurrentUser();
      
      // Timeout de segurança: se após 50 segundos ainda não recebeu o usuário completo,
      // bloqueia com erro. 50s cobre: 35s (HTTP user/current) + ~15s (ACF mainframe cold start).
      const safetyTimeout = setTimeout(() => {
        if (!this.isAppReady) {
          console.info('Timeout ao carregar usuário');
          this.loadingError = 'Não foi possível carregar os dados do usuário. Verifique sua conexão e tente novamente.';
        }
      }, 50000);
      
      this.authService.currentUser$.pipe(
        filter(user => user !== null),
        takeUntil(this.destroy$),
        switchMap(user => {
          this.currentUser = user;
          this.updateLoadingState('Validando acesso no mainframe...', 85);
          return this.authService.checkAcfAuthorization().pipe(
            catchError(err => {
              // Mainframe indisponível ou erro de rede — igual ao CIBR: bloqueia com mensagem
              const msg = (err as Error).message || 'Erro ao conectar com o mainframe';
              return of({ authorized: false, message: msg, mainframeUnavailable: true, canManageManagers: false, canManageParametros: false });
            })
          );
        })
      ).subscribe(acfResult => {
        clearTimeout(safetyTimeout);

        // Igual ao CIBR: só bloqueia se o mainframe estiver fora (erro de rede/503).
        // Se authorized=false mas mainframe respondeu, deixa o usuário entrar —
        // as restrições de acesso acontecem por ação dentro do sistema.
        if ((acfResult as any).mainframeUnavailable) {
          this.loadingError = acfResult.message || 'Não foi possível verificar acesso no mainframe. Tente novamente.';
          return;
        }

        // Registra as permissões de menu para uso no nav e nos guards de rota
        this.canManageManagers = acfResult.canManageManagers ?? false;
        this.canManageParametros = acfResult.canManageParametros ?? false;
        this.authService.setMenuPermissions(this.canManageManagers, this.canManageParametros);

        this.updateLoadingState('Finalizando...', 95);
        if (this.currentUser) {
          this.insightsService.setAuthenticatedUser(this.currentUser.samAccountName);
          this.insightsService.trackTrace('app_ready', undefined, {
            version: '1.0',
            user: this.currentUser.samAccountName
          });
          this.startTelemetryHeartbeat(this.currentUser.samAccountName);
        }
        setTimeout(() => {
          this.updateLoadingState('Pronto!', 100);
          setTimeout(() => {
            this.isAppReady = true;
          }, 300);
        }, 400);
      });
    } else {
      // Se não estiver logado, libera a tela (será redirecionado pelo guard)
      this.updateLoadingState('Redirecionando para login...', 50);
      setTimeout(() => {
        this.isAppReady = true;
      }, 500);
    }
  }

  getUserInitials(): string {
    if (!this.currentUser?.fullName) { return ''; }
    const names = this.currentUser.fullName.trim().split(' ');
    if (names.length === 1) { return names[0].substring(0, 2).toUpperCase(); }
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  }
}
